import { useState, useEffect } from "react";
import COLORS from "../config/colors";
import AnimatedNumber from "./AnimatedNumber";
import { fetchCapacity, fetchRevenueConfig } from "../lib/supabase";
import { getAllSlugs } from "../config/markets";

export default function CapacityDashboard({ styles }) {
  const [capacity, setCapacity] = useState([]);
  const [config, setConfig] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState("all");
  const markets = getAllSlugs();

  useEffect(() => {
    fetchCapacity(selectedMarket === "all" ? null : selectedMarket).then(setCapacity);
    fetchRevenueConfig().then(setConfig);
  }, [selectedMarket]);

  const tiers = config?.tiers || [
    { from_week: 1, to_week: 6, max_per_week: 50000 },
    { from_week: 7, to_week: 12, max_per_week: 75000 },
    { from_week: 13, to_week: null, max_per_week: 100000 },
  ];
  const leadTimeDays = config?.lead_time_days || 30;

  // Aggregate by week across all markets if "all" selected
  const weeklyData = (() => {
    const byWeek = {};
    for (const row of capacity) {
      const key = row.week_start;
      if (!byWeek[key]) {
        byWeek[key] = { week_start: key, booked_revenue: 0, max_revenue: 0, project_count: 0, markets: [] };
      }
      byWeek[key].booked_revenue += Number(row.booked_revenue || 0);
      byWeek[key].max_revenue += Number(row.max_revenue || 0);
      byWeek[key].project_count += Number(row.project_count || 0);
      byWeek[key].markets.push(row.market_slug);
    }
    return Object.values(byWeek).sort((a, b) => a.week_start.localeCompare(b.week_start));
  })();

  const totalBooked = weeklyData.reduce((s, w) => s + w.booked_revenue, 0);
  const totalProjects = weeklyData.reduce((s, w) => s + w.project_count, 0);

  const sty = {
    section: { padding: 20, background: COLORS.white, borderRadius: 12, border: "1px solid rgba(155,142,130,0.15)" },
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: COLORS.bark, margin: 0 }}>
            Capacity & Scheduling
          </h2>
          <div style={{ fontSize: 12, color: COLORS.stone, marginTop: 4 }}>
            {leadTimeDays}-day lead time · Auto-scheduled on deposit
          </div>
        </div>
        <select
          value={selectedMarket}
          onChange={e => setSelectedMarket(e.target.value)}
          style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(155,142,130,0.3)", fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: COLORS.cream, textTransform: "capitalize" }}
        >
          <option value="all">All Markets</option>
          {markets.map(m => <option key={m} value={m}>{m.replace(/-/g, " ")}</option>)}
        </select>
      </div>

      {/* Revenue scaling tiers */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${tiers.length}, 1fr)`, gap: 12, marginBottom: 20 }}>
        {tiers.map((tier, i) => (
          <div key={i} style={{ ...sty.section, textAlign: "center" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: COLORS.stone, marginBottom: 6 }}>
              {tier.to_week ? `Weeks ${tier.from_week}–${tier.to_week}` : `Week ${tier.from_week}+`}
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.honey, fontFamily: "'Playfair Display', serif" }}>
              ${(tier.max_per_week / 1000).toFixed(0)}k
            </div>
            <div style={{ fontSize: 11, color: COLORS.stone }}>per market / week</div>
          </div>
        ))}
      </div>

      {/* Summary stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
        <div style={{ ...sty.section, textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: COLORS.stone, marginBottom: 4 }}>Total Booked</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.bark, fontFamily: "'Playfair Display', serif" }}>
            $<AnimatedNumber value={Math.round(totalBooked)} />
          </div>
        </div>
        <div style={{ ...sty.section, textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: COLORS.stone, marginBottom: 4 }}>Projects Scheduled</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.pine, fontFamily: "'Playfair Display', serif" }}>
            <AnimatedNumber value={totalProjects} />
          </div>
        </div>
        <div style={{ ...sty.section, textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: COLORS.stone, marginBottom: 4 }}>Markets Active</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.blue, fontFamily: "'Playfair Display', serif" }}>
            <AnimatedNumber value={new Set(capacity.map(c => c.market_slug)).size} />
          </div>
        </div>
      </div>

      {/* Weekly capacity bars */}
      <div style={sty.section}>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.bark, marginBottom: 16 }}>
          Weekly Schedule
        </div>

        {weeklyData.length === 0 && (
          <div style={{ textAlign: "center", padding: 32, color: COLORS.stone, fontSize: 14 }}>
            No projects scheduled yet. Capacity fills automatically when deposits are paid.
          </div>
        )}

        {weeklyData.map(week => {
          const pct = week.max_revenue > 0 ? (week.booked_revenue / week.max_revenue) * 100 : 0;
          const weekDate = new Date(week.week_start + "T00:00:00");
          const label = weekDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });

          return (
            <div key={week.week_start} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.bark }}>
                  Week of {label}
                  <span style={{ fontSize: 11, color: COLORS.stone, fontWeight: 400, marginLeft: 8 }}>
                    {week.project_count} project{week.project_count !== 1 ? "s" : ""}
                    {selectedMarket === "all" && week.markets.length > 0 && ` · ${[...new Set(week.markets)].length} market${[...new Set(week.markets)].length > 1 ? "s" : ""}`}
                  </span>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: pct > 85 ? COLORS.red : COLORS.bark }}>
                  ${Math.round(week.booked_revenue / 1000)}k / ${Math.round(week.max_revenue / 1000)}k
                </div>
              </div>
              <div style={{ height: 10, background: "rgba(155,142,130,0.1)", borderRadius: 5, overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  width: `${Math.min(pct, 100)}%`,
                  background: pct > 85 ? COLORS.red : pct > 60 ? COLORS.honey : COLORS.pine,
                  borderRadius: 5,
                  transition: "width 0.6s ease",
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
